import React, { createContext, useContext, useEffect, useRef, ReactNode } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth } from './AuthContext';

type WebSocketContextType = {
  socket: Socket | null;
  isConnected: boolean;
};

const WebSocketContext = createContext<WebSocketContextType>({
  socket: null,
  isConnected: false,
});

export const useWebSocket = () => useContext(WebSocketContext);

export const WebSocketProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const socketRef = useRef<Socket | null>(null);
  const [isConnected, setIsConnected] = React.useState(false);
  const { token } = useAuth();

  // Type for organization switch event data
  interface OrganizationSwitchData {
    organization_id: string;
    organization_name?: string;
  }

  // Handle organization switch events (user-specific, no token included)
  const handleOrganizationSwitch = React.useCallback(async (data: OrganizationSwitchData) => {
    console.log('Organization switched event received:', data);
    // ✅ SECURITY FIX: Token is NO LONGER sent via WebSocket
    // The OrganizationSwitcher component already handles the token update via login()
    // We don't need to reload - the UI will update automatically via React state
    // Reloading would clear localStorage and log the user out!
  }, []);

  useEffect(() => {
    // Don't connect if we don't have a token (user not logged in)
    if (!token) {
      console.log('WebSocket: No token, skipping connection');
      setIsConnected(false);
      return;
    }

    // Connect to the WebSocket server with authentication
    const wsUrl = process.env.REACT_APP_WS_URL;
    if (!wsUrl) {
      throw new Error('REACT_APP_WS_URL environment variable must be set');
    }

    console.log('WebSocket: Connecting with authentication...');
    const socket = io(wsUrl, {
      auth: {
        token: token  // ✅ SECURITY FIX: Pass JWT token for authentication
      },
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: 3,
      reconnectionDelay: 2000,
      timeout: 10000,
      forceNew: true,
      transports: ['websocket', 'polling']
    });
    socketRef.current = socket;

    const onConnect = () => {
      setIsConnected(true);
    };

    const onDisconnect = () => {
      setIsConnected(false);
    };

    const onDataUpdated = () => {
      // You can add logic here to refresh data when updates are received
    };

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.on('data_updated', onDataUpdated);
    socket.on('organization_switched', handleOrganizationSwitch);
    

    // Handle server shutdown signal
    const onServerShutdown = () => {
      setIsConnected(false);
      socket.removeAllListeners();
      socket.disconnect();
    };
    
    socket.on('server_shutdown', onServerShutdown);

    // Handle page unload to clean up WebSocket
    const handleBeforeUnload = () => {
      socket.removeAllListeners();
      socket.disconnect();
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Cleanup function
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.off('data_updated', onDataUpdated);
      socket.off('organization_switched', handleOrganizationSwitch);
      socket.off('server_shutdown', onServerShutdown);
      socket.removeAllListeners();  // Remove all listeners
      socket.disconnect();
    };
  }, [token, handleOrganizationSwitch]);  // Reconnect when token changes


  return (
    <WebSocketContext.Provider value={{ socket: socketRef.current, isConnected }}>
      {children}
    </WebSocketContext.Provider>
  );
};

export default WebSocketContext;
